import React, { createContext, useState, useEffect, useCallback, useRef, useContext } from 'react';
import { 
  getFirestore, 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  onSnapshot,
  deleteDoc,
  arrayRemove,
  updateDoc,
  query,  
  orderBy,
  limit,
  writeBatch,
  increment,
  where,
  serverTimestamp
} from 'firebase/firestore';
import UserContext from './UserContext';
import { getFirestoreInstance } from '../utils/firebaseHelper';
import { getAuth } from 'firebase/auth';
import { normalizeDataDates } from '../utils/dateUtils';
import { normalizeDate } from '../utils/dateUtils';

export const TourContext = createContext();

// Cache prefixes
const CACHE_PREFIX = 'tourCache_';
const SHOW_CACHE_PREFIX = 'showCache_';
const EXPENSE_CACHE_PREFIX = 'tourExpenseCache_';
const TOUR_LIST_CACHE_KEY = 'tourListCache';
const SHOW_LIST_CACHE_KEY = 'showListCache';
const TOUR_DATE_CACHE_PREFIX = 'tourDateCache_';
const SHOW_DATE_CACHE_PREFIX = 'showDateCache_';

// Cache helper functions
const saveToCache = (key, data, prefix = CACHE_PREFIX) => {
  const cacheData = {
    data,
    timestamp: Date.now()
  };
  localStorage.setItem(`${prefix}${key}`, JSON.stringify(cacheData));
};

const getFromCache = (key, prefix = CACHE_PREFIX) => {
  const cached = localStorage.getItem(`${prefix}${key}`);
  if (!cached) return null;

  try {
    return JSON.parse(cached).data;
  } catch (error) {
    localStorage.removeItem(`${prefix}${key}`);
    return null;
  }
};

const clearCache = (itemId = null, type = null) => {
  if (itemId) {
    if (type === 'expense') {
      localStorage.removeItem(`${EXPENSE_CACHE_PREFIX}${itemId}`);
    } else if (type === 'tour') {
      localStorage.removeItem(`${CACHE_PREFIX}${itemId}`);
      // Clear all tour date caches for this tour
      Object.keys(localStorage)
        .filter(key => key.startsWith(`${TOUR_DATE_CACHE_PREFIX}${itemId}_`))
        .forEach(key => localStorage.removeItem(key));
    } else if (type === 'show') {
      localStorage.removeItem(`${SHOW_CACHE_PREFIX}${itemId}`);
      // Clear all show-related caches
      Object.keys(localStorage)
        .filter(key => key.startsWith(`${SHOW_DATE_CACHE_PREFIX}${itemId}`))
        .forEach(key => localStorage.removeItem(key));
    } else {
      // Clear all caches related to this ID
      localStorage.removeItem(`${CACHE_PREFIX}${itemId}`);
      localStorage.removeItem(`${SHOW_CACHE_PREFIX}${itemId}`);
      localStorage.removeItem(`${EXPENSE_CACHE_PREFIX}${itemId}`);
      // Clear all related date caches
      Object.keys(localStorage)
        .filter(key => key.startsWith(`${TOUR_DATE_CACHE_PREFIX}${itemId}_`) || 
                       key.startsWith(`${SHOW_DATE_CACHE_PREFIX}${itemId}`))
        .forEach(key => localStorage.removeItem(key));
    }
  } else {
    // Clear all cache entries matching our prefixes
    Object.keys(localStorage)
      .filter(key => 
        key.startsWith(CACHE_PREFIX) || 
        key.startsWith(SHOW_CACHE_PREFIX) ||
        key.startsWith(EXPENSE_CACHE_PREFIX) ||
        key.startsWith(TOUR_DATE_CACHE_PREFIX) ||
        key.startsWith(SHOW_DATE_CACHE_PREFIX) ||
        key === TOUR_LIST_CACHE_KEY ||
        key === SHOW_LIST_CACHE_KEY
      )
      .forEach(key => localStorage.removeItem(key));
  }
};

export const TourProvider = ({ children }) => {
  const { userData } = useContext(UserContext);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [expenseHistory, setExpenseHistory] = useState({});
  const [isLoadingExpenses, setIsLoadingExpenses] = useState(false);
  const [hasMoreEvents, setHasMoreEvents] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Keep track of active listeners
  const eventListeners = useRef({});
  const dateListeners = useRef({});
  const expenseListeners = useRef({});
  const selectedEventRef = useRef();
  
  useEffect(() => {
    selectedEventRef.current = selectedEvent;
  }, [selectedEvent]);

  // Set up real-time listener for an event document (tour or show)
  const setupEventListener = useCallback((eventId, eventType, initialData) => {
    // If already listening, don't set up another listener
    if (eventListeners.current[eventId]) {
      console.log(`Already listening to ${eventType} ${eventId}, skipping`);
      return;
    }
    
    console.log(`Setting up event listener for ${eventType} ${eventId}`);
    const db = getFirestore();
    const collectionName = eventType === 'tour' ? 'tours' : 'shows';
    
    // Listen for changes to the event document
    eventListeners.current[eventId] = onSnapshot(
      doc(db, collectionName, eventId),
      async (snapshot) => {
        if (snapshot.exists()) {
          // Get current document data
          const eventData = snapshot.data();
          
          // Use existing dates rather than re-fetching them all
          const existingDates = events.find(e => e.id === eventId && e.type === eventType)?.eventDates || [];
          
          // Fetch pending invitations
          let pendingInvitationsData = [];
          if (eventData.pendingInvitations?.length > 0) {
            const invitationsPromises = eventData.pendingInvitations.map(async (invitationId) => {
              try {
                const invitationDoc = await getDoc(doc(db, 'TourInvitations', invitationId));
                return invitationDoc.exists() ? {
                  id: invitationDoc.id,
                  ...invitationDoc.data()
                } : null;
              } catch (error) {
                console.error(`Error fetching invitation ${invitationId}:`, error);
                return null;
              }
            });
            pendingInvitationsData = (await Promise.all(invitationsPromises)).filter(Boolean);
          }
          
          const updatedEventData = {
            id: eventId,
            type: eventType,
            ...eventData,
            // Ensure we use the correct name field for shows
            showName: eventType === 'show' ? (eventData.showName || eventData.tourName) : undefined,
            eventDates: existingDates, // Use eventDates as the standard field name
            // Keep tourDates for backward compatibility but copy from eventDates
            tourDates: existingDates,
            pendingInvitations: eventData.pendingInvitations || [],
            pendingInvitationsData
          };
          
          // Update cache
          saveToCache(eventId, updatedEventData, eventType === 'tour' ? CACHE_PREFIX : SHOW_CACHE_PREFIX);
          
          // Update state
          setEvents(prev => {
            const updatedEvents = prev.map(event => 
              (event.id === eventId && event.type === eventType) ? updatedEventData : event
            );
            return updatedEvents;
          });
          
          // Update selected event if needed
          if (selectedEventRef.current?.id === eventId && selectedEventRef.current?.type === eventType) {
            setSelectedEvent(updatedEventData);
          }
        }
      },
      error => {
        console.error(`Error in event listener for ${eventId}:`, error);
      }
    );
  }, [events]);
  

  // Setup individual date listener
  const setupDateListener = useCallback((eventId, dateId, eventType) => {
    // Create a unique key for this date
    const listenerKey = `${eventId}_${dateId}`;
    
    // If already listening, don't set up another listener
    if (dateListeners.current[listenerKey]) {
      console.log(`Already listening to ${eventType} date ${eventId}/${dateId}, skipping`);
      return;
    }
    
    console.log(`Setting up date listener for ${eventType} date ${eventId}/${dateId}`);
    
    const db = getFirestore();
    const collectionName = eventType === 'tour' ? 'tours' : 'shows';
    const dateCachePrefix = eventType === 'tour' ? TOUR_DATE_CACHE_PREFIX : SHOW_DATE_CACHE_PREFIX;
    
    // First check if we have cached date data
    const cachedDate = getFromCache(listenerKey, dateCachePrefix);
    
    // Both tours and shows have eventDates subcollection
    dateListeners.current[listenerKey] = onSnapshot(
      doc(db, collectionName, eventId, 'eventDates', dateId),
      (snapshot) => {
        if (snapshot.exists()) {
          const dateData = {
            id: dateId,
            ...snapshot.data()
          };
          
          // Normalize date field if it exists
          if (dateData.date) {
            dateData.date = normalizeDate(dateData.date);
          }
          
          // Save to cache
          saveToCache(listenerKey, dateData, dateCachePrefix);
          
          // Update state - only update this specific date
          setEvents(prev => {
            // Find the event
            const eventIndex = prev.findIndex(e => e.id === eventId && e.type === eventType);
            if (eventIndex === -1) return prev;
            
            // Create a new event object
            const updatedEvent = {...prev[eventIndex]};
            
            // Make sure eventDates exists
            if (!updatedEvent.eventDates) {
              updatedEvent.eventDates = [];
            }
            
            // Find and update the specific date
            const dateIndex = updatedEvent.eventDates.findIndex(d => d.id === dateId);
            
            // If date exists, update it; otherwise, add it
            if (dateIndex !== -1) {
              updatedEvent.eventDates = [
                ...updatedEvent.eventDates.slice(0, dateIndex),
                dateData,
                ...updatedEvent.eventDates.slice(dateIndex + 1)
              ];
            } else {
              updatedEvent.eventDates = [...updatedEvent.eventDates, dateData];
            }
            
            // For backward compatibility, also update tourDates
            updatedEvent.tourDates = [...updatedEvent.eventDates];
            
            // Create a new array of events
            return [
              ...prev.slice(0, eventIndex),
              updatedEvent,
              ...prev.slice(eventIndex + 1)
            ];
          });
          
          // Update selected event if needed
          if (selectedEventRef.current?.id === eventId && selectedEventRef.current?.type === eventType) {
            setSelectedEvent(prevSelectedEvent => {
              if (!prevSelectedEvent) return prevSelectedEvent;
              
              // Create a copy of the event
              const updatedEvent = {...prevSelectedEvent};
              
              // Make sure eventDates exists
              if (!updatedEvent.eventDates) {
                updatedEvent.eventDates = [];
              }
              
              // Find and update the specific date
              const dateIndex = updatedEvent.eventDates.findIndex(d => d.id === dateId);
              
              // If date exists, update it; otherwise, add it
              if (dateIndex !== -1) {
                updatedEvent.eventDates = [
                  ...updatedEvent.eventDates.slice(0, dateIndex),
                  dateData,
                  ...updatedEvent.eventDates.slice(dateIndex + 1)
                ];
              } else {
                updatedEvent.eventDates = [...updatedEvent.eventDates, dateData];
              }
              
              // For backward compatibility, also update tourDates
              updatedEvent.tourDates = [...updatedEvent.eventDates];
              
              return updatedEvent;
            });
          }
        } else if (eventType === 'show') {
          // Date document doesn't exist - if this is a show, try fetching from the main document as fallback
          // IMPORTANT: Use a one-time getDoc instead of another listener to prevent infinite loops
          console.log(`Date document ${eventId}/${dateId} not found, trying main show document as fallback`);
          
          getDoc(doc(db, collectionName, eventId)).then(mainSnapshot => {
            if (mainSnapshot.exists()) {
              const showData = mainSnapshot.data();
              
              // Create a date object from the main document
              const dateData = {
                id: dateId,
                ...showData,
                // Ensure there's a date field
                date: showData.date || showData.eventDate || new Date().toISOString().split('T')[0]
              };
              
              // Normalize the date
              if (dateData.date) {
                dateData.date = normalizeDate(dateData.date);
              }
              
              // Save to cache
              saveToCache(listenerKey, dateData, dateCachePrefix);
              
              // Update the dates in the events state
              setEvents(prev => {
                const eventIndex = prev.findIndex(e => e.id === eventId && e.type === 'show');
                if (eventIndex === -1) return prev;
                
                const updatedEvent = {
                  ...prev[eventIndex],
                  showName: showData.showName || showData.tourName,
                };
                
                // Make sure eventDates exists
                if (!updatedEvent.eventDates) {
                  updatedEvent.eventDates = [];
                }
                
                // Update or add the date
                const dateIndex = updatedEvent.eventDates.findIndex(d => d.id === dateId);
                if (dateIndex !== -1) {
                  updatedEvent.eventDates = [
                    ...updatedEvent.eventDates.slice(0, dateIndex),
                    dateData,
                    ...updatedEvent.eventDates.slice(dateIndex + 1)
                  ];
                } else {
                  updatedEvent.eventDates = [...updatedEvent.eventDates, dateData];
                }
                
                // For backward compatibility, also update tourDates
                updatedEvent.tourDates = [...updatedEvent.eventDates];
                
                return [
                  ...prev.slice(0, eventIndex),
                  updatedEvent,
                  ...prev.slice(eventIndex + 1)
                ];
              });
              
              // Update selected event if needed
              if (selectedEventRef.current?.id === eventId && selectedEventRef.current?.type === 'show') {
                setSelectedEvent(prevSelectedEvent => {
                  if (!prevSelectedEvent) return prevSelectedEvent;
                  
                  // Create a copy of the event
                  const updatedEvent = {
                    ...prevSelectedEvent,
                    showName: showData.showName || showData.tourName,
                  };
                  
                  // Make sure eventDates exists
                  if (!updatedEvent.eventDates) {
                    updatedEvent.eventDates = [];
                  }
                  
                  // Find and update the specific date
                  const dateIndex = updatedEvent.eventDates.findIndex(d => d.id === dateId);
                  
                  // If date exists, update it; otherwise, add it
                  if (dateIndex !== -1) {
                    updatedEvent.eventDates = [
                      ...updatedEvent.eventDates.slice(0, dateIndex),
                      dateData,
                      ...updatedEvent.eventDates.slice(dateIndex + 1)
                    ];
                  } else {
                    updatedEvent.eventDates = [...updatedEvent.eventDates, dateData];
                  }
                  
                  // For backward compatibility, also update tourDates
                  updatedEvent.tourDates = [...updatedEvent.eventDates];
                  
                  return updatedEvent;
                });
              }
            }
          }).catch(error => {
            console.error(`Error in show fallback fetch for ${eventId}:`, error);
          });
        }
      },
      error => {
        console.error(`Error in date listener for ${eventId}/${dateId}:`, error);
        // Remove this listener on error to prevent continued attempts
        if (dateListeners.current[listenerKey]) {
          dateListeners.current[listenerKey]();
          delete dateListeners.current[listenerKey];
        }
      }
    );
    
    return cachedDate;
  }, []);
  

  // Handle event selection - now also sets up listeners for dates
  const ensureDatesLoaded = useCallback(async (event) => {
    if (!event || !event.id || !event.type) return event;
    
    // Check if dates are missing or empty
    if (!Array.isArray(event.eventDates) || event.eventDates.length === 0) {
      console.log(`Event ${event.id} has no eventDates, fetching them now...`);
      
      try {
        const db = getFirestoreInstance();
        const collectionName = event.type === 'tour' ? 'tours' : 'shows';
        
        // For both tours and shows, fetch dates from eventDates subcollection
        const datesSnapshot = await getDocs(collection(db, collectionName, event.id, 'eventDates'));
        
        if (!datesSnapshot.empty) {
          console.log(`Found ${datesSnapshot.size} dates for ${event.type} ${event.id}`);
          
          // Map the docs to the expected structure
          const dates = datesSnapshot.docs.map(dateDoc => ({
            id: dateDoc.id,
            ...dateDoc.data(),
          }));
          
          // Normalize all dates
          const normalizedDates = dates.map(date => {
            if (date.date) {
              date.date = normalizeDate(date.date);
            }
            return date;
          });
          
          // Create a new event object with the fetched dates
          const updatedEvent = {
            ...event,
            eventDates: normalizedDates,
            // For backward compatibility
            tourDates: normalizedDates
          };
          
          // Update the cache
          const cachePrefix = event.type === 'tour' ? CACHE_PREFIX : SHOW_CACHE_PREFIX;
          saveToCache(event.id, updatedEvent, cachePrefix);
          console.log(`Updated cache with fetched dates for ${event.id}`);
          
          return updatedEvent;
        } else {
          console.log(`No dates found in eventDates subcollection for ${event.type} ${event.id}`);
          
          // For shows, if no dates in subcollection, create a single date from main document as fallback
          if (event.type === 'show') {
            console.log(`Fetching main document for show ${event.id} as fallback`);
            const showDoc = await getDoc(doc(db, collectionName, event.id));
            
            if (showDoc.exists()) {
              const showData = showDoc.data();
              
              // Create a single date entry from the main document
              const dates = [{
                id: 'date1',
                ...showData,
                // Ensure there's a date field
                date: showData.date || showData.eventDate || new Date().toISOString().split('T')[0]
              }];
              
              // Normalize the date
              dates[0].date = normalizeDate(dates[0].date);
              
              const updatedEvent = {
                ...event,
                ...showData,
                showName: showData.showName || showData.tourName,
                eventDates: dates,
                tourDates: dates // For backward compatibility
              };
              
              // Update the cache
              saveToCache(event.id, updatedEvent, SHOW_CACHE_PREFIX);
              
              return updatedEvent;
            }
          }
          
          // Return original event with empty dates array
          const updatedEvent = {
            ...event,
            eventDates: [],
            tourDates: [] // For backward compatibility
          };
          
          const cachePrefix = event.type === 'tour' ? CACHE_PREFIX : SHOW_CACHE_PREFIX;
          saveToCache(event.id, updatedEvent, cachePrefix);
          return updatedEvent;
        }
      } catch (error) {
        console.error(`Error fetching dates for event ${event.id}:`, error);
        // Return original event object if there's an error
        return event;
      }
    }
    
    // If dates already exist, just return the event
    return event;
  }, []);

  // Fetch full event data with smart caching and listeners
  const fetchEventDetails = useCallback(async (eventId, eventType, forceRefresh = false) => {
    if (!eventId || !eventType) return null;
  
    console.log(`Fetching ${eventType} details for ${eventId}, forceRefresh: ${forceRefresh}`);
    
    try {
      const cachePrefix = eventType === 'tour' ? CACHE_PREFIX : SHOW_CACHE_PREFIX;
      
      // First check the cache if not forcing refresh
      if (!forceRefresh) {
        const cachedEvent = getFromCache(eventId, cachePrefix);
        if (cachedEvent) {
          console.log(`Found cached ${eventType} data for ${eventId}`);
          
          // Validate that the cached event has the expected structure
          if (Array.isArray(cachedEvent.eventDates)) {
            console.log(`Cache validation passed: eventDates is an array with ${cachedEvent.eventDates.length} items`);
            
            // Set up listener in the background
            setupEventListener(eventId, eventType, cachedEvent);
            return cachedEvent;
          } else {
            console.log(`Cache validation failed: eventDates is not an array or is missing`);
          }
        }
      }
  
      console.log(`Fetching fresh data from Firestore for ${eventType} ${eventId}`);
      
      // If not in cache or forced refresh, fetch from Firestore
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Step 1: Get the main event document
      const eventDoc = await getDoc(doc(db, collectionName, eventId));
      
      if (!eventDoc.exists()) {
        console.log(`${eventType} ${eventId} not found in Firestore`);
        return null;
      }
      
      const eventData = eventDoc.data();
      console.log(`Found ${eventType} data for ${eventId}: ${eventData.tourName || eventData.showName || 'Unnamed Event'}`);
      
      // Step 2: Get dates from eventDates subcollection for both tours and shows
      let dates = [];
      
      // Use eventDates subcollection for both tours and shows
      const datesSnapshot = await getDocs(collection(db, collectionName, eventId, 'eventDates'));
      
      if (!datesSnapshot.empty) {
        dates = datesSnapshot.docs.map(dateDoc => ({
          id: dateDoc.id,
          ...dateDoc.data(),
        }));
        
        console.log(`Found ${dates.length} dates for ${eventType} ${eventId}`);
      } else {
        // Fallback for shows without subcollection (backward compatibility)
        if (eventType === 'show') {
          console.log(`No eventDates subcollection found for show ${eventId}, using main document as fallback`);
          dates = [{
            id: 'date1',  // Default ID for single show date
            ...eventData,
            date: eventData.date || eventData.eventDate || new Date().toISOString().split('T')[0] // Ensure a date field exists
          }];
        }
      }
  
      // Step 3: Fetch pending invitations if they exist
      let pendingInvitationsData = [];
      if (eventData.pendingInvitations?.length > 0) {
        console.log(`Fetching ${eventData.pendingInvitations.length} pending invitations`);
        const invitationsPromises = eventData.pendingInvitations.map(async (invitationId) => {
          try {
            const invitationDoc = await getDoc(doc(db, 'TourInvitations', invitationId));
            return invitationDoc.exists() ? {
              id: invitationDoc.id,
              ...invitationDoc.data()
            } : null;
          } catch (error) {
            console.error(`Error fetching invitation ${invitationId}:`, error);
            return null;
          }
        });
        pendingInvitationsData = (await Promise.all(invitationsPromises)).filter(Boolean);
        console.log(`Successfully fetched ${pendingInvitationsData.length} pending invitations`);
      }
  
      // Step 4: Assemble the complete event data
      const fullEventData = normalizeDataDates({
        id: eventId,
        type: eventType,
        ...eventData,
        // Ensure we use the correct name field for shows
        showName: eventType === 'show' ? (eventData.showName || eventData.tourName) : undefined,
        eventDates: dates, // Use eventDates as the standard field name
        // Keep tourDates for backward compatibility but copy from eventDates
        tourDates: dates,
        pendingInvitations: eventData.pendingInvitations || [],
        pendingInvitationsData
      });
  
      // Step 5: Save to cache before setting up listeners
      saveToCache(eventId, fullEventData, cachePrefix);
      console.log(`Saved complete ${eventType} data to cache for ${eventId}`);
      
      // Step 6: Set up listener for real-time updates
      setupEventListener(eventId, eventType, fullEventData);
      console.log(`Set up real-time listener for ${eventType} ${eventId}`);
      
      return fullEventData;
    } catch (error) {
      console.error(`Error fetching ${eventType} details for ${eventId}:`, error);
      return null;
    }
  }, [setupEventListener]);

  // Fetch a specific date with listener
  const fetchDate = useCallback(async (eventId, dateId, eventType) => {
    if (!eventId || !dateId || !eventType) return null;
    
    // First check if we have a listener for this date
    const listenerKey = `${eventId}_${dateId}`;
    const dateCachePrefix = eventType === 'tour' ? TOUR_DATE_CACHE_PREFIX : SHOW_DATE_CACHE_PREFIX;
    
    // Check cache first - if we have cached data, return it immediately
    const cachedDate = getFromCache(listenerKey, dateCachePrefix);
    if (cachedDate) {
      console.log(`Found cached data for ${eventType} date ${eventId}/${dateId}`);
      
      // Set up listener in the background if not already listening
      if (!dateListeners.current[listenerKey]) {
        setupDateListener(eventId, dateId, eventType);
      }
      
      return cachedDate;
    }
    
    // If no cached data, fetch directly
    console.log(`No cached data for ${eventType} date ${eventId}/${dateId}, fetching...`);
    
    try {
      const db = getFirestore();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // First try to get date from eventDates subcollection (for both tours and shows)
      const dateDoc = await getDoc(doc(db, collectionName, eventId, 'eventDates', dateId));
      
      if (dateDoc.exists()) {
        const dateData = {
          id: dateId,
          ...dateDoc.data()
        };
        
        // Normalize date if it exists
        if (dateData.date) {
          dateData.date = normalizeDate(dateData.date);
        }
        
        // Save to cache
        saveToCache(listenerKey, dateData, dateCachePrefix);
        
        // Set up listener for future updates
        if (!dateListeners.current[listenerKey]) {
          setupDateListener(eventId, dateId, eventType);
        }
        
        return dateData;
      } else if (eventType === 'show') {
        // Fallback for shows: check the main document
        const showDoc = await getDoc(doc(db, collectionName, eventId));
        
        if (showDoc.exists()) {
          const showData = showDoc.data();
          
          // Create a date object with the show data
          const dateData = {
            id: dateId,
            ...showData,
            // Ensure there's a date field
            date: showData.date || showData.eventDate || new Date().toISOString().split('T')[0]
          };
          
          // Normalize the date
          if (dateData.date) {
            dateData.date = normalizeDate(dateData.date);
          }
          
          // Save to cache
          saveToCache(listenerKey, dateData, dateCachePrefix);
          
          // Set up listener for future updates
          if (!dateListeners.current[listenerKey]) {
            setupDateListener(eventId, dateId, eventType);
          }
          
          return dateData;
        }
      }
      
      return null;
    } catch (error) {
      console.error(`Error fetching date ${eventId}/${dateId}:`, error);
      return null;
    }
  }, [setupDateListener]);

  // Clean up single event listener
  const cleanupEventListener = useCallback((eventId) => {
    if (eventListeners.current[eventId]) {
      eventListeners.current[eventId]();
      delete eventListeners.current[eventId];
    }
    
    // Clean up all date listeners for this event
    Object.keys(dateListeners.current)
      .filter(key => key.startsWith(`${eventId}_`))
      .forEach(key => {
        dateListeners.current[key]();
        delete dateListeners.current[key];
      });
  }, []);

  // Clean up all listeners
  const cleanupAllListeners = useCallback(() => {
    Object.keys(eventListeners.current).forEach(eventId => {
      if (eventListeners.current[eventId]) {
        eventListeners.current[eventId]();
      }
    });
    eventListeners.current = {};
    
    // Clean up all date listeners
    Object.keys(dateListeners.current).forEach(key => {
      dateListeners.current[key]();
    });
    dateListeners.current = {};
    
    // Clean up expense listeners
    Object.keys(expenseListeners.current).forEach(key => {
      expenseListeners.current[key]();
    });
    expenseListeners.current = {};
  }, []);

  // Fetch events data with pagination
  const fetchEventsData = useCallback(async (userData, page = 1, pageSize = 4) => {
    if (!userData?.userId) {
      console.log("Cannot fetch events: No user ID provided");
      return;
    }
  
    console.log(`Fetching events data for user ${userData.userId}, page ${page}, pageSize ${pageSize}`);
    setIsLoading(true);
    
    try {
      const db = getFirestoreInstance();
      
      // Get user's events from the 'assignedEvents' subcollection with pagination
      const eventsQuery = query(
        collection(db, 'users', userData.userId, 'assignedEvents'),
        orderBy('assignedAt', 'desc'),
        limit(pageSize * page) // Get all events up to the current page
      );
      
      const eventsSnapshot = await getDocs(eventsQuery);
      
      if (eventsSnapshot.empty) {
        console.log("No events found for user");
        setEvents([]);
        setHasMoreEvents(false);
        setIsLoading(false);
        return;
      }
      
      // Extract event data and organize by type
      const eventsByType = {
        tour: [],
        show: []
      };
      
      eventsSnapshot.docs.forEach(doc => {
        const eventData = doc.data();
        const eventType = eventData.type || 'tour';
        
        // Make sure we have the necessary IDs
        if (eventType === 'tour' && eventData.tourId) {
          eventsByType.tour.push({
            eventId: eventData.tourId,
            assignedDates: eventData.assignedDates || [],
            permissions: eventData.permissions || {},
            data: eventData
          });
        } else if (eventType === 'show' && eventData.showId) {
          eventsByType.show.push({
            eventId: eventData.showId,
            assignedDates: eventData.assignedDates || [],
            permissions: eventData.permissions || {},
            data: eventData
          });
        }
      });
      
      console.log(`Found ${eventsByType.tour.length} tours and ${eventsByType.show.length} shows for user ${userData.userId}`);
      
      // Check if there are more events
      setHasMoreEvents(eventsSnapshot.docs.length >= pageSize * page);
      
      // Calculate which events to fetch for the current page
      const startIndex = (page - 1) * pageSize;
      
      // Combine tours and shows, then paginate
      const allEvents = [
        ...eventsByType.tour.map(e => ({ ...e, type: 'tour' })),
        ...eventsByType.show.map(e => ({ ...e, type: 'show' }))
      ];
      
      const eventsToFetch = allEvents.slice(startIndex, startIndex + pageSize);
      console.log(`Fetching details for ${eventsToFetch.length} events on page ${page}`);
      
      // Set current page
      setCurrentPage(page);
      
      // Fetch full data for events in the current page
      const eventsDataPromises = eventsToFetch.map(event => 
        fetchEventDetails(event.eventId, event.type)
      );
      
      const eventsData = await Promise.all(eventsDataPromises);
      const validEvents = eventsData.filter(Boolean);
      console.log(`Successfully fetched ${validEvents.length} valid events`);
      
      // Ensure all dates are loaded for each event and normalize dates
      const eventsWithDates = await Promise.all(validEvents.map(async event => {
        // Store the original assignment data that we'll need later
        const assignmentData = eventsToFetch.find(e => 
          e.eventId === event.id && e.type === event.type
        )?.data;
        
        // First ensure dates are loaded
        const eventWithDates = await ensureDatesLoaded(event);
        
        // Then normalize all date strings in the event's dates
        if (eventWithDates.eventDates && Array.isArray(eventWithDates.eventDates)) {
          eventWithDates.eventDates = eventWithDates.eventDates.map(date => {
            // Normalize the date format to YYYY-MM-DD
            if (date.date) {
              date.date = normalizeDate(date.date);
            }
            return date;
          });
          
          // For backward compatibility
          eventWithDates.tourDates = [...eventWithDates.eventDates];
        }
        
        // Add the assignment data to the event object for use in filtering
        return {
          ...eventWithDates,
          // Store the assignment data for later use in RightMenu
          data: assignmentData
        };
      }));
      console.log(`Ensured dates are loaded and normalized for all events`);
      
      // Update state with fetched events
      if (page === 1) {
        setEvents(eventsWithDates);
      } else {
        setEvents(prev => {
          // Create a map for quick lookup of existing events
          const existingEventsMap = new Map(
            prev.map(event => [`${event.type}_${event.id}`, event])
          );
          
          // Merge new events, replacing any existing ones
          eventsWithDates.forEach(event => {
            existingEventsMap.set(`${event.type}_${event.id}`, event);
          });
          
          // Convert map back to array
          return Array.from(existingEventsMap.values());
        });
      }
  
      // Update selected event if needed
      if (selectedEventRef.current) {
        const updatedSelectedEvent = validEvents.find(
          e => e.id === selectedEventRef.current.id && e.type === selectedEventRef.current.type
        );
        
        if (updatedSelectedEvent) {
          console.log(`Updating selected event to match newly fetched data for ${updatedSelectedEvent.id}`);
          // Use handleEventSelection instead of setSelectedEvent directly
          handleEventSelection(updatedSelectedEvent, true);
        }
      } else if (validEvents.length > 0) {
        // If no event is selected, select the first one
        console.log(`No event was selected, selecting first event: ${validEvents[0].id}`);
        handleEventSelection(validEvents[0], true);
      }
    } catch (error) {
      console.error('Error fetching events data:', error);
      // Set default state to avoid UI errors
      if (page === 1) {
        setEvents([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [fetchEventDetails, ensureDatesLoaded]);

  // Load more events (next page)
  const loadMoreEvents = useCallback(async (userData) => {
    if (hasMoreEvents && !isLoading) {
      await fetchEventsData(userData, currentPage + 1);
    }
  }, [fetchEventsData, hasMoreEvents, isLoading, currentPage]);

  // Set up expense listener
  const setupExpenseListener = useCallback((eventId) => {
    // If already listening, don't set up another listener
    if (expenseListeners.current[eventId]) return;
    
    const db = getFirestore();
    
    expenseListeners.current[eventId] = onSnapshot(
      doc(db, 'ExpenseRecords', eventId),
      (snapshot) => {
        if (snapshot.exists()) {
          const expenseData = snapshot.data().dates || {};
          
          // Update cache
          saveToCache(eventId, expenseData, EXPENSE_CACHE_PREFIX);
          
          // Update state
          setExpenseHistory(prevHistory => ({
            ...prevHistory,
            [eventId]: expenseData
          }));
        }
      },
      error => {
        console.error(`Error in expense listener for ${eventId}:`, error);
      }
    );
  }, []);

  // Clean up expense listener
  const cleanupExpenseListener = useCallback((eventId) => {
    if (expenseListeners.current[eventId]) {
      expenseListeners.current[eventId]();
      delete expenseListeners.current[eventId];
    }
  }, []);

  // Expense handling
  const fetchExpenseHistory = useCallback(async (eventId) => {
    if (!eventId) return;
  
    setIsLoadingExpenses(true);
    try {
      // Check cache first
      const cachedExpenses = getFromCache(eventId, EXPENSE_CACHE_PREFIX);
      if (cachedExpenses) {
        setExpenseHistory(prevHistory => ({
          ...prevHistory,
          [eventId]: cachedExpenses
        }));
        
        // Set up expense listener
        setupExpenseListener(eventId);
        return;
      }
  
      // Fetch from Firestore
      const db = getFirestore();
      const expenseRef = doc(db, 'ExpenseRecords', eventId);
      const expenseDoc = await getDoc(expenseRef);
      
      const expenseData = expenseDoc.exists() 
        ? (expenseDoc.data().dates || {})
        : {};
  
      // Update state and cache
      setExpenseHistory(prevHistory => ({
        ...prevHistory,
        [eventId]: expenseData
      }));
      
      saveToCache(eventId, expenseData, EXPENSE_CACHE_PREFIX);
      
      // Set up expense listener
      setupExpenseListener(eventId);
    } catch (error) {
      console.error('Error fetching expense history:', error);
      setExpenseHistory(prevHistory => ({
        ...prevHistory,
        [eventId]: {}
      }));
    } finally {
      setIsLoadingExpenses(false);
    }
  }, [setupExpenseListener]);
  
  const updateExpenseHistory = useCallback(async (eventId) => {
    clearCache(eventId, 'expense');
    await fetchExpenseHistory(eventId);
  }, [fetchExpenseHistory]);

  // Event updates
  const updateEventData = useCallback(async (eventId, eventType) => {
    try {
      const cachePrefix = eventType === 'tour' ? 'tour' : 'show';
      clearCache(eventId, cachePrefix);
      const freshData = await fetchEventDetails(eventId, eventType, true);
      
      if (freshData) {
        setEvents(prev => prev.map(event => 
          (event.id === eventId && event.type === eventType) ? freshData : event
        ));

        if (selectedEventRef.current?.id === eventId && selectedEventRef.current?.type === eventType) {
          setSelectedEvent(freshData);
        }
      }
    } catch (error) {
      console.error(`Error updating ${eventType} data:`, error);
    }
  }, [fetchEventDetails]);

  // Update a specific date
  const updateDate = useCallback(async (eventId, dateId, eventType) => {
    try {
      console.log(`Performing comprehensive update for ${eventType} date ${eventId}/${dateId}`);
      
      // Step 1: Clear all related caches
      const listenerKey = `${eventId}_${dateId}`;
      const dateCachePrefix = eventType === 'tour' ? TOUR_DATE_CACHE_PREFIX : SHOW_DATE_CACHE_PREFIX;
      const eventCachePrefix = eventType === 'tour' ? CACHE_PREFIX : SHOW_CACHE_PREFIX;
      
      localStorage.removeItem(`${dateCachePrefix}${listenerKey}`);
      localStorage.removeItem(`${eventCachePrefix}${eventId}`);
      
      // Step 2: Explicitly fetch fresh data from Firestore
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      let dateData;
      
      if (eventType === 'tour') {
        const dateDoc = await getDoc(doc(db, collectionName, eventId, 'eventDates', dateId));
        
        if (!dateDoc.exists()) {
          console.error(`Tour date ${eventId}/${dateId} not found in Firestore`);
          return null;
        }
        
        // Get fresh tour date data
        dateData = {
          id: dateId,
          ...dateDoc.data()
        };
      } else {
        // For shows, check the eventDates subcollection first
        const dateDoc = await getDoc(doc(db, collectionName, eventId, 'eventDates', dateId));
        
        if (dateDoc.exists()) {
          dateData = {
            id: dateId,
            ...dateDoc.data()
          };
        } else {
          // Fallback to the main document for shows
          const showDoc = await getDoc(doc(db, collectionName, eventId));
          
          if (!showDoc.exists()) {
            console.error(`Show ${eventId} not found in Firestore`);
            return null;
          }
          
          // Get fresh show data
          const showData = showDoc.data();
          dateData = {
            id: dateId,
            ...showData,
            // Ensure there's a date field
            date: showData.date || showData.eventDate || new Date().toISOString().split('T')[0]
          };
        }
      }
      
      // Normalize date if it exists
      if (dateData.date) {
        dateData.date = normalizeDate(dateData.date);
      }
      
      // Save to cache
      saveToCache(listenerKey, dateData, dateCachePrefix);
      console.log(`Updated cache for ${eventType} date ${eventId}/${dateId}`);
      
      // Step 3: Update the events array
      setEvents(prev => {
        // Find the event
        const eventIndex = prev.findIndex(e => e.id === eventId && e.type === eventType);
        if (eventIndex === -1) return prev;
        
        // Create a new event object
        const updatedEvent = {...prev[eventIndex]};
        
        // Find and update the specific date
        const dateIndex = updatedEvent.dates?.findIndex(d => d.id === dateId);
        
        // Normalize the date data
        const normalizedDateData = normalizeDataDates(dateData);
        
        // If date exists, update it; otherwise, add it
        if (dateIndex !== -1 && updatedEvent.dates) {
          updatedEvent.dates = [
            ...updatedEvent.dates.slice(0, dateIndex),
            normalizedDateData,
            ...updatedEvent.dates.slice(dateIndex + 1)
          ];
        } else {
          updatedEvent.dates = [...(updatedEvent.dates || []), normalizedDateData];
        }
        
        // Create a new array of events
        return [
          ...prev.slice(0, eventIndex),
          updatedEvent,
          ...prev.slice(eventIndex + 1)
        ];
      });
      
      // Step 4: Update selectedEvent if needed
      if (selectedEventRef.current?.id === eventId && selectedEventRef.current?.type === eventType) {
        setSelectedEvent(prevSelectedEvent => {
          if (!prevSelectedEvent) return prevSelectedEvent;
          
          // Create a new dates array with the updated date
          const updatedDates = [...(prevSelectedEvent.dates || [])];
          const dateIndex = updatedDates.findIndex(d => d.id === dateId);
          
          if (dateIndex !== -1) {
            updatedDates[dateIndex] = dateData;
          } else {
            updatedDates.push(dateData);
          }
          
          return {
            ...prevSelectedEvent,
            dates: updatedDates
          };
        });
      }
      
      // Step 5: Reset the listener to ensure it picks up future changes
      // First, remove any existing listener
      if (dateListeners.current[listenerKey]) {
        dateListeners.current[listenerKey]();
        delete dateListeners.current[listenerKey];
      }
      
      // Set up a fresh listener
      setupDateListener(eventId, dateId, eventType);
      
      console.log(`Comprehensive update completed for ${eventType} date ${eventId}/${dateId}`);
      return dateData;
    } catch (error) {
      console.error(`Error in comprehensive update for date ${eventId}/${dateId}:`, error);
      return null;
    }
  }, [setupDateListener]);

  // Delete an event and clean up its listeners
  const deleteEvent = useCallback(async (eventId, eventType, userId) => {
    try {
      // Confirm the operation with the user first
      if (!window.confirm(`Are you sure you want to delete this ${eventType}? This action cannot be undone.`)) {
        return false;
      }
  
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      const eventRef = doc(db, collectionName, eventId);
      
      // Get the event data first to identify users and files to delete
      const eventDoc = await getDoc(eventRef);
      if (!eventDoc.exists()) {
        throw new Error(`${eventType} not found`);
      }
      
      const eventData = eventDoc.data();
      
      // Only allow deletion if the current user is the creator
      if (userId !== eventData.createdBy?.userId) {
        throw new Error(`Only the creator can delete the ${eventType}`);
      }
      
      // 1. Collect all users who need to have their stats updated
      const usersToUpdate = [];
      
      // Add the creator
      usersToUpdate.push({
        userId: eventData.createdBy.userId,
        role: 'creator'
      });
      
      // Add all artists
      if (eventData.artistDetails && Array.isArray(eventData.artistDetails)) {
        eventData.artistDetails.forEach(artist => {
          if (artist.userId !== eventData.createdBy.userId) { // Avoid duplicates
            usersToUpdate.push({
              userId: artist.userId,
              role: 'artist'
            });
          }
        });
      }
      
      // Add all crew members
      if (eventData.crewDetails && Array.isArray(eventData.crewDetails)) {
        eventData.crewDetails.forEach(crew => {
          if (crew.userId !== eventData.createdBy.userId) { // Avoid duplicates
            usersToUpdate.push({
              userId: crew.userId,
              role: 'crew'
            });
          }
        });
      }
      
      // Add all managers
      if (eventData.managerDetails) {
        if (Array.isArray(eventData.managerDetails)) {
          eventData.managerDetails.forEach(manager => {
            if (manager.userId !== eventData.createdBy.userId) { // Avoid duplicates
              usersToUpdate.push({
                userId: manager.userId,
                role: 'manager'
              });
            }
          });
        } else if (typeof eventData.managerDetails === 'object') {
          // Handle object structure
          Object.values(eventData.managerDetails).forEach(manager => {
            if (manager.userId !== eventData.createdBy.userId) { // Avoid duplicates
              usersToUpdate.push({
                userId: manager.userId,
                role: 'manager'
              });
            }
          });
        }
      }
      
      // Add all organizations
      if (eventData.organizationDetails && Array.isArray(eventData.organizationDetails)) {
        eventData.organizationDetails.forEach(org => {
          if (org.userId !== eventData.createdBy.userId) { // Avoid duplicates
            usersToUpdate.push({
              userId: org.userId,
              role: 'organization'
            });
          }
        });
      }
      
      // Add all vendors
      if (eventData.vendorDetails && Array.isArray(eventData.vendorDetails)) {
        eventData.vendorDetails.forEach(vendor => {
          usersToUpdate.push({
            userId: vendor.userId,
            role: 'vendor'
          });
        });
      }
      
      // Add all staff
      if (eventData.staffDetails && Array.isArray(eventData.staffDetails)) {
        eventData.staffDetails.forEach(staff => {
          usersToUpdate.push({
            userId: staff.userId,
            role: 'staff'
          });
        });
      }
      
      console.log(`Found ${usersToUpdate.length} users to update for ${eventType} deletion`);
      
      // 2. Collect all dates to find files to delete
      let dates = [];
      if (eventType === 'tour') {
        try {
          const datesRef = collection(db, collectionName, eventId, 'eventDates');
          const datesSnapshot = await getDocs(datesRef);
          
          dates = datesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log(`Found ${dates.length} dates to process`);
        } catch (error) {
          console.error(`Error fetching dates:`, error);
          // Continue with the process even if we can't get all dates
        }
      } else {
        // For shows, first try to get dates from eventDates subcollection
        try {
          const datesRef = collection(db, collectionName, eventId, 'eventDates');
          const datesSnapshot = await getDocs(datesRef);
          
          if (!datesSnapshot.empty) {
            dates = datesSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            console.log(`Found ${dates.length} dates to process for show`);
          } else {
            // Fallback to the main document for shows
            dates = [{
              id: 'date1',
              ...eventData
            }];
          }
        } catch (error) {
          console.error(`Error fetching dates:`, error);
          // Fallback to the main document
          dates = [{
            id: 'date1',
            ...eventData
          }];
        }
      }
      
      // 3. Collect all file paths that need to be deleted
      const filesToDelete = [];
      
      for (const date of dates) {
        // Collect attachment file paths
        if (date.schedule?.attachments && Array.isArray(date.schedule.attachments)) {
          date.schedule.attachments.forEach(attachment => {
            if (attachment.filePath) {
              filesToDelete.push(attachment.filePath);
            }
            if (attachment.thumbnailPath && attachment.thumbnailPath !== attachment.filePath) {
              filesToDelete.push(attachment.thumbnailPath);
            }
          });
        }
        
        // Collect tech rider file paths
        if (date.event?.techrider?.techRiders && Array.isArray(date.event.techrider.techRiders)) {
          date.event.techrider.techRiders.forEach(techRider => {
            if (techRider.filePath) {
              filesToDelete.push(techRider.filePath);
            }
            if (techRider.thumbnailPath && techRider.thumbnailPath !== techRider.filePath) {
              filesToDelete.push(techRider.thumbnailPath);
            }
          });
        }
        
        // Collect ticket file paths from date documents
        if (date.tickets && Array.isArray(date.tickets)) {
          date.tickets.forEach(ticket => {
            if (ticket.ticketFilePath) {
              filesToDelete.push(ticket.ticketFilePath);
            }
          });
        }
      }
      
      // Look for tickets in the main event document as well (for backward compatibility)
      if (eventData.ticketDetails && Array.isArray(eventData.ticketDetails)) {
        eventData.ticketDetails.forEach(dateTickets => {
          if (dateTickets.tickets && Array.isArray(dateTickets.tickets)) {
            dateTickets.tickets.forEach(ticket => {
              if (ticket.ticketFilePath) {
                filesToDelete.push(ticket.ticketFilePath);
              }
            });
          }
        });
      }
      
      console.log(`Found ${filesToDelete.length} files to delete`);
      
      // 4. Delete all files
      const deletePromises = [];
      if (filesToDelete.length > 0) {
        for (const filePath of filesToDelete) {
          try {
            const authHeader = await getIdToken();
            deletePromises.push(
              deleteFile(filePath, authHeader).catch(error => {
                console.error(`Error deleting file ${filePath}:`, error);
                return { success: false, filePath, error };
              })
            );
          } catch (error) {
            console.error(`Error setting up file deletion for ${filePath}:`, error);
          }
        }
        
        // Wait for all file deletions to complete (or fail)
        if (deletePromises.length > 0) {
          const fileResults = await Promise.allSettled(deletePromises);
          const failedFiles = fileResults
            .filter(result => result.status === 'rejected' || (result.value && !result.value.success))
            .map(result => result.status === 'rejected' ? result.reason : result.value);
          
          if (failedFiles.length > 0) {
            console.warn(`Failed to delete ${failedFiles.length} files:`, failedFiles);
          }
        }
      }
      
      // 5. Update user stats and remove event references for each user
      const batch = writeBatch(db);
      
      for (const user of usersToUpdate) {
        try {
          // Find all assignedEvents documents for this event
          const assignedEventsRef = collection(db, 'users', user.userId, 'assignedEvents');
          let eventQuery;
          
          if (eventType === 'tour') {
            eventQuery = query(assignedEventsRef, where("tourId", "==", eventId));
          } else {
            eventQuery = query(assignedEventsRef, where("showId", "==", eventId));
          }
          
          const assignedEventsSnapshot = await getDocs(eventQuery);
          
          // Delete all assignedEvents documents for this event
          assignedEventsSnapshot.docs.forEach(doc => {
            batch.delete(doc.ref);
          });
          
          // Update user stats - we'll decrement based on type
          const updateData = {};
          
          if (eventType === 'tour') {
            // For tours, decrement tourCount by 1
            updateData.toursCount = increment(-1);
            
            // For tour dates, decrement showsCount by the number of dates assigned to this user
            if (assignedEventsSnapshot.size > 0) {
              updateData.showsCount = increment(-assignedEventsSnapshot.size);
            }
          } else {
            // For shows, just decrement showsCount by 1
            updateData.showsCount = increment(-1);
          }
          
          // Apply the stats update if needed
          if (Object.keys(updateData).length > 0) {
            batch.update(doc(db, 'users', user.userId), updateData);
          }
        } catch (userError) {
          console.error(`Error updating user ${user.userId}:`, userError);
          // Continue with other users even if one fails
        }
      }
      
      // Delete any user_event_dates documents related to this event
      if (eventType === 'tour') {
        try {
          const userEventDatesRef = collection(db, 'user_event_dates');
          const userEventDatesQuery = query(userEventDatesRef, where("tourId", "==", eventId));
          const userEventDatesSnapshot = await getDocs(userEventDatesQuery);
          
          userEventDatesSnapshot.docs.forEach(doc => {
            batch.delete(doc.ref);
          });
          
          console.log(`Added ${userEventDatesSnapshot.size} user_event_dates documents for deletion`);
        } catch (error) {
          console.error('Error preparing user_event_dates documents for deletion:', error);
        }
      }
      
      // For tours and shows, delete all dates in the eventDates subcollection
      try {
        const datesRef = collection(db, collectionName, eventId, 'eventDates');
        const datesSnapshot = await getDocs(datesRef);
        
        datesSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
        
        console.log(`Added ${datesSnapshot.size} dates for deletion`);
      } catch (error) {
        console.error(`Error preparing dates for deletion: ${error}`);
      }
      
      // Delete assignedUsers subcollection
      try {
        const assignedUsersRef = collection(db, collectionName, eventId, 'assignedUsers');
        const assignedUsersSnapshot = await getDocs(assignedUsersRef);
        
        assignedUsersSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
        
        console.log(`Added ${assignedUsersSnapshot.size} assignedUsers documents for deletion`);
      } catch (error) {
        console.error('Error preparing assignedUsers documents for deletion:', error);
      }
      
      // Finally, delete the main event document
      batch.delete(eventRef);
      
      // Clean up event and expense listeners
      cleanupEventListener(eventId);
      cleanupExpenseListener(eventId);
      
      // Commit all the batched operations
      await batch.commit();
      
      // Update the events array in the context
      setEvents(prev => prev.filter(event => !(event.id === eventId && event.type === eventType)));
      
      // Update selected event if needed
      if (selectedEventRef.current?.id === eventId && selectedEventRef.current?.type === eventType) {
        if (events.length > 1) {
          // Find a different event to select
          const nextEvent = events.find(event => !(event.id === eventId && event.type === eventType));
          setSelectedEvent(nextEvent);
        } else {
          // No more events
          setSelectedEvent(null);
        }
      }
      
      return true;
    } catch (error) {
      console.error(`Error deleting ${eventType}:`, error);
      return false;
    }
  }, [cleanupEventListener, cleanupExpenseListener, events]);

  const deleteFile = async (filePath, authToken) => {
    try {
      if (!filePath || !authToken) {
        return { success: false, error: 'Missing filePath or authToken' };
      }
      
      console.log('Deleting file from Backblaze B2:', filePath);
      
      // Use the deleteBackblazeFile endpoint
      const response = await fetch('/api/deleteBackblazeFile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ filePath })
      });
      
      // Check the response
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete file');
        } else {
          const errorText = await response.text();
          console.error('Non-JSON error response:', errorText);
          throw new Error('Server returned an unexpected response format');
        }
      }
      
      const responseData = await response.json();
      console.log('Delete successful, response:', responseData);
      
      return { success: true };
    } catch (error) {
      console.error(`Error deleting file '${filePath}':`, error);
      return { success: false, error: error.message };
    }
  };
  
  // Helper function to get the current user's ID token
  const getIdToken = async () => {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }
      
      return await currentUser.getIdToken();
    } catch (error) {
      console.error('Error getting ID token:', error);
      throw error;
    }
  };
  
  // Event selection handler
  const handleEventSelection = useCallback(async (event, autoSelectDate = true) => {
    if (!event) {
      setSelectedEvent(null);
      setSelectedDate(null);
      return;
    }
  
    try {
      // If event is already in state, just select it but ensure it has dates
      if (events.some(e => e.id === event.id && e.type === event.type)) {
        let fullEvent = events.find(e => e.id === event.id && e.type === event.type);
        
        // Ensure dates are loaded
        fullEvent = await ensureDatesLoaded(fullEvent);
        
        setSelectedEvent(fullEvent);
        
        // Auto-select the latest date if no date is selected and autoSelectDate is true
        if (!selectedDate && autoSelectDate && fullEvent.dates?.length > 0) {
          // Sort dates to get the latest one (most recent)
          const sortedDates = [...fullEvent.dates].sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB - dateA; // Descending order (latest first)
          });
          
          const latestDate = sortedDates[0];
          setSelectedDate(latestDate.id);
          setupDateListener(event.id, latestDate.id, event.type);
        } else if (selectedDate) {
          setupDateListener(event.id, selectedDate, event.type);
        }
        return;
      }
  
      // Otherwise fetch it
      let fullEventData = await fetchEventDetails(event.id, event.type);
      
      if (fullEventData) {
        // Ensure dates are loaded
        fullEventData = await ensureDatesLoaded(fullEventData);
        
        // Add to events array if not already there
        setEvents(prev => {
          if (!prev.some(e => e.id === fullEventData.id && e.type === fullEventData.type)) {
            return [...prev, fullEventData];
          }
          return prev;
        });
        
        setSelectedEvent(fullEventData);
        
        // Auto-select the latest date if no date is selected and autoSelectDate is true
        if (!selectedDate && autoSelectDate && fullEventData.dates?.length > 0) {
          // Sort dates to get the latest one (most recent)
          const sortedDates = [...fullEventData.dates].sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB - dateA; // Descending order (latest first)
          });
          
          const latestDate = sortedDates[0];
          setSelectedDate(latestDate.id);
          setupDateListener(fullEventData.id, latestDate.id, fullEventData.type);
        } else if (selectedDate) {
          setupDateListener(fullEventData.id, selectedDate, fullEventData.type);
        }
      }
    } catch (error) {
      console.error('Error in handleEventSelection:', error);
    }
  }, [fetchEventDetails, events, selectedDate, setupDateListener, ensureDatesLoaded]);

  // Select a specific date
  const selectDate = useCallback((eventId, dateId, eventType) => {
    const event = events.find(e => e.id === eventId && e.type === eventType);
    if (event) {
      setSelectedEvent(event);
      setSelectedDate(dateId);
      
      // Set up listener for this date
      setupDateListener(eventId, dateId, eventType);
    }
  }, [events, setupDateListener]);

  // Utility functions
  const getSelectedDate = useCallback(() => {
    if (!selectedEvent?.eventDates || !selectedDate) return null;
    
    // Find the selected date in the event dates
    return selectedEvent.eventDates.find(date => date.id === selectedDate) || null;
  }, [selectedEvent, selectedDate]);

  const getSelectedSectionData = useCallback((section) => {
    const eventDate = getSelectedDate();
    if (!eventDate) return null;

    switch (section) {
      case 'schedule': return eventDate.schedule || {};
      case 'event': return eventDate.event || {};
      case 'hotel': return eventDate.hotel || [];
      case 'route':
        if (!eventDate.schedule?.scheduleList) return [];
        return eventDate.schedule.scheduleList.filter(
          schedule => schedule.isTravelling && schedule.fromLocation && schedule.toLocation
        );
      default: return null;
    }
  }, [getSelectedDate]);

  // Save event list to cache
  const saveEventListToCache = (eventIds, eventType, page = 1) => {
    try {
      const cacheKey = eventType === 'tour' ? TOUR_LIST_CACHE_KEY : SHOW_LIST_CACHE_KEY;
      
      // Get existing cache or initialize new
      const existingCache = localStorage.getItem(cacheKey);
      let cacheData = existingCache ? JSON.parse(existingCache) : { eventIds: [], timestamp: Date.now() };
      
      // For first page, replace the cache
      if (page === 1) {
        cacheData = { eventIds, timestamp: Date.now() };
      } else {
        // For subsequent pages, append unique IDs
        const uniqueIds = [...new Set([...cacheData.eventIds, ...eventIds])];
        cacheData = { eventIds: uniqueIds, timestamp: Date.now() };
      }
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error(`Error saving ${eventType} list to cache:`, error);
    }
  };

  // Get event list from cache
  const getEventListFromCache = (eventType) => {
    try {
      const cacheKey = eventType === 'tour' ? TOUR_LIST_CACHE_KEY : SHOW_LIST_CACHE_KEY;
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;
      
      const cacheData = JSON.parse(cached);
      return cacheData.eventIds;
    } catch (error) {
      console.error(`Error getting ${eventType} list from cache:`, error);
      
      const cacheKey = eventType === 'tour' ? TOUR_LIST_CACHE_KEY : SHOW_LIST_CACHE_KEY;
      localStorage.removeItem(cacheKey);
      return null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // We won't clean up listeners on unmount anymore
      // They'll be cleaned up on logout instead
    };
  }, []);

  return (
    <TourContext.Provider value={{
      selectedEvent,
      setSelectedEvent: handleEventSelection,
      events,
      setEvents,
      selectedDate,
      setSelectedDate: (dateId) => {
        setSelectedDate(dateId);
        if (dateId && selectedEvent?.id && selectedEvent?.type) {
          setupDateListener(selectedEvent.id, dateId, selectedEvent.type);
        }
      },
      isLoading,
      fetchEventsData,
      loadMoreEvents,
      hasMoreEvents,
      updateEventData,
      updateDate,
      selectDate,
      getSelectedDate,
      getSelectedSectionData,
      currentSection,
      setCurrentSection,
      expenseHistory,
      isLoadingExpenses,
      fetchExpenseHistory,
      updateExpenseHistory,
      deleteEvent,
      cleanupAllListeners,
      clearCache
    }}>
      {children}
    </TourContext.Provider>
  );
};

export default TourContext;